import * as React from 'react';

import type { LucideIcon } from 'lucide-react';

import { cn } from '../../../lib/utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface FormSectionHeaderProps {
  className?: string;
  icon: LucideIcon;
  /** Optional muted hint rendered after the title (e.g. "Shipper → KLAREO"). Accepts any ReactNode. */
  suffix?: React.ReactNode;
  /** Section title. Accepts any ReactNode for rich content (e.g. translated strings, badge nodes). */
  title: React.ReactNode;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Section header with a small primary-tinted icon badge on the left.
 * Used in form drawers and as the secondary sub-block header inside DrawerSection children.
 *
 * The optional `suffix` prop renders muted text after the title
 * (e.g. "Shipper → KLAREO") — used by the shippers drawer to indicate flow direction.
 */
export function FormSectionHeader({ className, icon: Icon, suffix, title }: FormSectionHeaderProps): React.JSX.Element {
  return (
    <div className={cn('flex items-center gap-2', className)}>
      <div className="flex items-center justify-center w-6 h-6 rounded-md bg-primary/10 text-primary shrink-0">
        <Icon className="w-3.5 h-3.5" />
      </div>
      <span className="text-sm font-semibold text-foreground">{title}</span>
      {suffix ? <span className="font-normal text-muted-foreground">{suffix}</span> : null}
    </div>
  );
}
